/**
 * ЛОГИКА ЭКРАНА «СТАТИСТИКА» · Ф2.4 — раскладка того, что уже посчитало ядро.
 *
 * ⛔ Здесь нет ни одной СВОЕЙ цифры: и словарь по частям речи, и «узнаю/достаю», и
 * лестница Nation, и семьи слов приходят из `core/progress.py` через `GET /progress`.
 * Витрина решает только КАК это уложить на экран: чем сгруппировать хвост частей речи,
 * какую ступень лестницы подсветить, как поделить полосу на три доли без потери процента.
 *
 * ⛔ Запреты ядра действуют и здесь (`progress.py`, шапка модуля):
 *   • никакого вердикта об уровне («твой уровень B1») — метки CEFR ставила модель;
 *   • никакого «зная N слов, понимаешь X% речи» — покрытие текста ≠ понимание.
 *     Научная цифра покрытия С ИСТОЧНИКОМ («~95% устной речи», Nation) — законна, она
 *     приезжает готовой строкой из ядра и мы её не сочиняем.
 *
 * ⚠ Чего на экране НЕТ и почему: активность по дням и распределение по боксам живут в
 * `reviews`/`words`, но наружу их не отдаёт ни одна read-only функция ядра. Рисовать
 * график, наполненный догадкой, нельзя — поэтому блоков нет вовсе (замер и предложение
 * аддитивного поля — в плане окна).
 *
 * Ни DOM, ни сети — поэтому раскладка проверяется запуском:
 *   cd Lang/web && node tools/check-screens.mjs
 */

/** Кусок словаря по одной части речи — ровно как его отдаёт `vocabulary_stats.by_pos`. */
export interface PosBucket {
  total: number;
  new: number;
  learning: number;
  known: number;
}

/** Строка шкалы «из чего словарь»: подпись, счётчик и три доли полосы. */
export interface PosRow {
  key: string;
  label: string;
  hint: string;            // из чего собрано «Прочее» (у обычной строки пусто)
  total: number;
  known: number;
  learning: number;
  fresh: number;           // ещё не начинали (status='new')
  pctKnown: number;
  pctLearning: number;
  pctNew: number;
}

/** Русские подписи частей речи — во множественном числе, как их читает человек. */
const POS_RU: Record<string, string> = {
  noun: "Существительные",
  verb: "Глаголы",
  adjective: "Прилагательные",
  adverb: "Наречия",
  phrase: "Выражения",
  preposition: "Предлоги",
  pronoun: "Местоимения",
  conjunction: "Союзы",
  determiner: "Определители",
  numeral: "Числительные",
  interjection: "Междометия",
};

/** Родительный падеж — для перечисления внутри «Прочее (предлогов, местоимений…)». */
const POS_RU_GEN: Record<string, string> = {
  preposition: "предлоги",
  pronoun: "местоимения",
  conjunction: "союзы",
  determiner: "определители",
  numeral: "числительные",
  interjection: "междометия",
  adjective: "прилагательные",
  adverb: "наречия",
  phrase: "выражения",
  noun: "существительные",
  verb: "глаголы",
};

/**
 * Части речи, которые всегда стоят своей строкой. Остальные складываются в «Прочее»:
 * в словаре их 11 видов, и 6 строк по два десятка слов — это не плотность, а шум.
 */
export const MAIN_POS = ["noun", "verb", "adjective", "adverb", "phrase"];

export function posLabel(key: string): string {
  return POS_RU[key] ?? (key === "null" ? "Без разметки" : key);
}

/**
 * Три доли полосы в процентах. Последняя досчитывается вычитанием, а не округлением, —
 * иначе на 1416 словах полоса не сойдётся в 100% и справа останется щель.
 */
export function shares(b: PosBucket): { pctKnown: number; pctLearning: number; pctNew: number } {
  const total = b.total || 0;
  if (total <= 0) return { pctKnown: 0, pctLearning: 0, pctNew: 0 };
  const pctKnown = Math.round(((b.known || 0) / total) * 100);
  const pctLearning = Math.round(((b.learning || 0) / total) * 100);
  const pctNew = Math.max(0, 100 - pctKnown - pctLearning);
  return { pctKnown, pctLearning, pctNew };
}

/**
 * Словарь по частям речи → строки шкалы: сперва крупные части речи по убыванию,
 * последним «Прочее» с перечислением того, что в него сложилось.
 */
export function posRows(byPos: Record<string, PosBucket>): PosRow[] {
  const rows: PosRow[] = [];
  const rest: { key: string; b: PosBucket }[] = [];

  for (const [key, b] of Object.entries(byPos || {})) {
    if (!b || !b.total) continue;
    if (MAIN_POS.includes(key)) rows.push(makeRow(key, posLabel(key), "", b));
    else rest.push({ key, b });
  }
  rows.sort((a, z) => z.total - a.total);

  if (rest.length) {
    rest.sort((a, z) => z.b.total - a.b.total);
    const sum = rest.reduce(
      (acc, r) => ({
        total: acc.total + r.b.total,
        new: acc.new + (r.b.new || 0),
        learning: acc.learning + (r.b.learning || 0),
        known: acc.known + (r.b.known || 0),
      }),
      { total: 0, new: 0, learning: 0, known: 0 },
    );
    // Перечисляем РЕАЛЬНЫЙ состав, а не «и т.п.»: три самых крупных и многоточие.
    const names = rest.map((r) => POS_RU_GEN[r.key] ?? r.key);
    const hint = names.slice(0, 3).join(" · ") + (names.length > 3 ? " · …" : "");
    rows.push(makeRow("other", "Прочее", hint, sum));
  }
  return rows;
}

function makeRow(key: string, label: string, hint: string, b: PosBucket): PosRow {
  const s = shares(b);
  return {
    key, label, hint,
    total: b.total,
    known: b.known || 0,
    learning: b.learning || 0,
    fresh: b.new || 0,
    ...s,
  };
}

/* ── лестница Nation ──────────────────────────────────────────────────────── */

export interface Rung {
  label: string;
  low: number;
  high: number;
  what: string;
  src: string;
}

export interface RungView {
  rung: Rung;
  here: boolean;        // ступень, на которую сейчас идём
  ceiling: boolean;     // сюда упрётся словарь, если выучить его весь
  passed: boolean;      // ступень уже взята
}

/**
 * Единицы словаря → семьи слов ОДНОЙ цифрой (серединой диапазона ×0.85–0.87).
 * Формула ровно та же, что в `progress.py` (`mid_learned`), — иначе подсветка ступени
 * разъедется с текстом «вы здесь», который считает ядро.
 */
export function familyMid(units: number, ratio: [number, number]): number {
  return Math.round(units * ((ratio[0] + ratio[1]) / 2));
}

/**
 * Разметка лестницы: где мы и где потолок словаря.
 * «Вы здесь» — ПЕРВАЯ ступень, чья верхняя граница ещё не пройдена: это та, которую
 * сейчас берём. Всё, что ниже неё, помечено взятым.
 */
export function ladderView(rungs: Rung[], hereFam: number, ceilFam: number): RungView[] {
  const hereIdx = indexFor(rungs, hereFam);
  const ceilIdx = indexFor(rungs, ceilFam);
  return rungs.map((rung, i) => ({
    rung,
    here: i === hereIdx,
    ceiling: i === ceilIdx,
    passed: i < hereIdx,
  }));
}

function indexFor(rungs: Rung[], fam: number): number {
  for (let i = 0; i < rungs.length; i++) {
    if (fam <= rungs[i].high) return i;
  }
  return rungs.length - 1;        // выше последней ступени — держимся за неё
}

/* ── народный ориентир владельца ──────────────────────────────────────────── */

export interface ScaleStep { name: string; verb: number; noun: number }

export interface ScaleBar { label: string; value: number; target: number; pct: number }

/**
 * Первая ступень народной шкалы в виде двух полос. Это ОРИЕНТИР, а не норма:
 * доказательной нормы по частям речи не существует (проверено 22.07, см. `progress.py`).
 */
export function scaleBars(scale: ScaleStep[], verbs: number, nouns: number): ScaleBar[] {
  const base = scale && scale.length ? scale[0] : null;
  if (!base) return [];
  return [
    { label: "Глаголы", value: verbs, target: base.verb, pct: pctOf(verbs, base.verb) },
    { label: "Существительные", value: nouns, target: base.noun, pct: pctOf(nouns, base.noun) },
  ];
}

export function pctOf(value: number, target: number): number {
  if (!target || target <= 0) return 0;
  return Math.max(0, Math.min(100, Math.round((value / target) * 100)));
}
